import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import ReceiptDialog from "@/components/ReceiptDialog";
import { fmt, setCurrency } from "@/lib/format";
import { DownloadSimple, MagnifyingGlass, Coins, ShoppingCart, Package } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RANGES = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "7d" },
  { key: "30d", label: "30d" },
  { key: "mtd", label: "MTD" },
];

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [settings, setSettings] = useState({ outlet_name: "Hizli" });
  const [selected, setSelected] = useState(null);
  const [period, setPeriod] = useState("30d");
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");

  useEffect(() => {
    api.get("/orders?limit=500").then((r) => setOrders(r.data));
    api.get("/settings").then((r) => { setSettings(r.data); setCurrency(r.data.currency); });
  }, []);

  useEffect(() => {
    api.get(`/orders/summary?period=${period}`).then((r) => setSummary(r.data));
  }, [period]);

  const filteredOrders = useMemo(() => {
    if (!summary) return orders;
    const { from, to } = summary.range;
    return orders.filter((o) => {
      if (o.created_at < from || o.created_at >= to) return false;
      if (sourceFilter !== "all" && (o.source || "in_store") !== sourceFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !String(o.order_number).includes(q) &&
          !(o.staff_name || "").toLowerCase().includes(q) &&
          !(o.aggregator_order_id || "").toLowerCase().includes(q) &&
          !(o.items || []).some((it) => (it.name || "").toLowerCase().includes(q))
        ) return false;
      }
      return true;
    });
  }, [orders, summary, search, sourceFilter]);

  const listedTotal = useMemo(
    () => filteredOrders.filter((o) => o.status === "completed").reduce((s, o) => s + (o.total || 0), 0),
    [filteredOrders],
  );
  const listedQty = useMemo(
    () => filteredOrders.filter((o) => o.status === "completed").reduce((s, o) => s + (o.items || []).reduce((q, it) => q + (it.quantity || 0), 0), 0),
    [filteredOrders],
  );

  const downloadCSV = async () => {
    try {
      const token = localStorage.getItem("boba_token");
      const outletId = localStorage.getItem("boba_outlet") || "";
      const res = await fetch(`${BACKEND_URL}/api/orders/export.csv?period=${period}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(outletId ? { "X-Outlet-Id": outletId } : {}),
        },
      });
      if (!res.ok) {
        toast.error("Download failed");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `orders_${period}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  return (
    <div className="h-screen flex flex-col">
      <div className="p-6 bg-white border-b border-boba-stoneLine">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-bold">Orders</h1>
            <p className="text-sm text-boba-inkSoft mt-1">All completed sales (times in IST)</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex bg-boba-cream border border-boba-stoneLine rounded-md p-1" data-testid="range-picker">
              {RANGES.map((r) => (
                <button
                  key={r.key}
                  onClick={() => setPeriod(r.key)}
                  className={`px-3 py-1.5 rounded text-xs font-mono font-semibold transition-colors ${period === r.key ? "bg-boba-matcha text-white" : "text-boba-inkSoft hover:bg-white"}`}
                  data-testid={`range-${r.key}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search order # / staff / agg. order # / item"
                className="h-9 pl-9 pr-3 border border-boba-stoneLine rounded-md text-sm w-72"
                data-testid="orders-search"
              />
            </div>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="h-9 px-3 border border-boba-stoneLine rounded-md text-sm bg-white"
              data-testid="orders-source-filter"
            >
              <option value="all">All sources</option>
              <option value="in_store">In-store</option>
              <option value="swiggy">Swiggy</option>
              <option value="zomato">Zomato</option>
            </select>
            <button
              onClick={downloadCSV}
              className="h-9 px-4 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md font-medium text-sm flex items-center gap-2"
              data-testid="download-csv"
            >
              <DownloadSimple size={16} weight="bold" /> Download CSV
            </button>
          </div>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5" data-testid="orders-totals">
          <TotalCard label="Total Sales" value={fmt(summary?.revenue ?? 0)} icon={Coins} accent="bg-boba-matcha/10 text-boba-matcha" />
          <TotalCard label="Orders" value={summary?.orders ?? 0} icon={ShoppingCart} accent="bg-boba-peach/10 text-boba-peach" />
          <TotalCard label="Quantity Sold" value={summary?.quantity_sold ?? 0} icon={Package} accent="bg-boba-honey/20 text-boba-ink" />
          <TotalCard label="Avg. Ticket" value={fmt(summary?.avg_ticket ?? 0)} icon={Coins} accent="bg-stone-100 text-stone-700" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scroll-thin p-6">
        <div className="bg-white border border-boba-stoneLine rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-boba-muted text-boba-inkSoft text-xs uppercase tracking-widest font-mono">
              <tr>
                <th className="text-left px-4 py-3">Order #</th>
                <th className="text-left px-4 py-3">Date & Time (IST)</th>
                <th className="text-left px-4 py-3">Source</th>
                <th className="text-left px-4 py-3">Staff</th>
                <th className="text-left px-4 py-3">Items</th>
                <th className="text-right px-4 py-3">Qty</th>
                <th className="text-left px-4 py-3">Payment</th>
                <th className="text-right px-4 py-3">Total</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody data-testid="orders-table">
              {filteredOrders.map((o) => {
                const qty = (o.items || []).reduce((s, it) => s + (it.quantity || 0), 0);
                const src = o.source || "in_store";
                const srcStyle = src === "swiggy" ? "bg-[#FC8019]/10 text-[#B85A0F] border-[#FC8019]/30"
                  : src === "zomato" ? "bg-[#E23744]/10 text-[#A62432] border-[#E23744]/30"
                  : "bg-stone-100 text-stone-600 border-stone-200";
                const srcLabel = src === "swiggy" ? "Swiggy" : src === "zomato" ? "Zomato" : "In-store";
                return (
                  <tr key={o.id} onClick={() => setSelected(o)} className="border-t border-boba-stoneLine cursor-pointer hover:bg-boba-muted/40" data-testid={`order-row-${o.order_number}`}>
                    <td className="px-4 py-3 font-mono font-semibold">#{o.order_number}</td>
                    <td className="px-4 py-3 text-boba-inkSoft font-mono">{new Date(o.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border font-mono ${srcStyle}`} data-testid={`order-source-${o.order_number}`}>{srcLabel}</span>
                      {o.aggregator_order_id && (
                        <div className="text-[10px] text-boba-inkSoft font-mono mt-0.5">#{o.aggregator_order_id}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">{o.staff_name}</td>
                    <td className="px-4 py-3 text-boba-inkSoft">{o.items.length} line{o.items.length !== 1 ? "s" : ""}</td>
                    <td className="px-4 py-3 text-right font-mono">{qty}</td>
                    <td className="px-4 py-3 capitalize">{o.payment_method}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-boba-matcha">{fmt(o.total)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${o.status === "completed" ? "bg-boba-matcha/10 text-boba-matcha" : o.status === "open" ? "bg-boba-honey/30 text-boba-ink" : "bg-red-100 text-boba-danger"}`}>{o.status}</span>
                    </td>
                  </tr>
                );
              })}
              {filteredOrders.length === 0 && (
                <tr><td colSpan={9} className="text-center py-12 text-boba-inkSoft">No orders in this range</td></tr>
              )}
            </tbody>
            {filteredOrders.length > 0 && (
              <tfoot className="bg-boba-cream border-t-2 border-boba-stoneLine">
                <tr>
                  <td colSpan={5} className="px-4 py-3 font-mono uppercase tracking-widest text-xs text-boba-inkSoft">Listed totals ({filteredOrders.length} orders)</td>
                  <td className="px-4 py-3 text-right font-mono font-bold">{listedQty}</td>
                  <td></td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-boba-matcha">{fmt(listedTotal)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <ReceiptDialog order={selected} settings={settings} onClose={() => setSelected(null)} />
    </div>
  );
}

function TotalCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-boba-stoneLine rounded-md p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-boba-inkSoft font-mono">{label}</div>
        <div className={`w-7 h-7 rounded-md flex items-center justify-center ${accent}`}><Icon size={16} weight="duotone" /></div>
      </div>
      <div className="font-mono text-xl font-bold mt-2 text-boba-ink">{value}</div>
    </div>
  );
}
